import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { CatalogCategory, CatalogSearchResults } from './types'

/** Matches the apis-repo `PgCatalogRepository` / BFF default (UID-227). */
export const DEFAULT_PAGE_SIZE = 20

export interface CatalogSearchOptions {
  /** Opaque token from a previous page's `nextCursor`. Mutually exclusive with `page`. */
  readonly cursor?: string
  /** 1-based page number for a direct/random-access jump. Mutually exclusive with `cursor`. */
  readonly page?: number
  readonly limit?: number
  /**
   * Exact-match catalog category filter (UID-33). Composes with `query` —
   * an empty `query` + a `category` is a valid category-only browse (the
   * BFF only 400s when BOTH are absent, per ADR-0090/routes.ts).
   */
  readonly category?: string
  /**
   * Exact-match FACET filter (UID-384 / ADR-0120 Decision 7.2), threaded
   * through verbatim to `GET /catalog/search`'s `specs[<name>]=<value>` param
   * — one value per facet name; several names conjoin. Backs the item-
   * granular "View all N items" grid's own facet filtering; `CatalogSeriesPage`
   * no longer needs this (UID-387 retired `useFilteredSeriesCodes.ts`'s
   * item-search-and-intersect workaround now that `/categories/{category}/series`
   * itself accepts `specs` and narrows existentially server-side).
   */
  readonly specs?: Readonly<Record<string, string>>
}

/**
 * The data-layer seam for the catalog search screen (UID-32, UID-227,
 * UID-33), mirroring `usePartsLibrary.ts`'s `PartsLibraryService` pattern: an
 * injectable interface so tests never hit the network, defaulting to the
 * REAL BFF-backed implementation.
 */
export interface CatalogSearchService {
  search(query: string, options: CatalogSearchOptions): Promise<CatalogSearchResults>
  /**
   * Catalog categories, sorted (UID-33), enriched with a BFF-derived
   * `imageUrl`/`seriesCount` — backs the category browse facet
   * (`GET /catalog/categories`, query-independent: every category in the
   * catalog, not scoped to the current keyword).
   */
  listCategories(): Promise<readonly CatalogCategory[]>
}

/** Pull the `title` off a Problem+JSON error body for a user-facing message
 * (same mapping `usePartsLibrary.ts`'s `problemTitle` uses). */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

async function bffSearch(
  query: string,
  options: CatalogSearchOptions,
): Promise<CatalogSearchResults> {
  const { data, error } = await apiClient.GET('/catalog/search', {
    params: {
      query: {
        // UID-33: omit `q` entirely rather than sending `q=''` for a
        // category-only browse — the BFF treats a blank `q` as absent
        // either way (routes.ts trims it), but omitting is the honest
        // request shape for "no keyword, browsing by category".
        ...(query.length > 0 ? { q: query } : {}),
        ...(options.cursor !== undefined ? { cursor: options.cursor } : {}),
        ...(options.page !== undefined ? { page: options.page } : {}),
        ...(options.limit !== undefined ? { limit: options.limit } : {}),
        ...(options.category !== undefined ? { category: options.category } : {}),
        ...(options.specs !== undefined && Object.keys(options.specs).length > 0
          ? { specs: options.specs }
          : {}),
      },
    },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t search the catalog. Try again.'))
  }
  return data as unknown as CatalogSearchResults
}

async function bffListCategories(): Promise<readonly CatalogCategory[]> {
  const { data, error } = await apiClient.GET('/catalog/categories', {})
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load catalog categories.'))
  }
  return data.categories
}

export const defaultCatalogSearchService: CatalogSearchService = {
  search: bffSearch,
  listCategories: bffListCategories,
}

export type CatalogSearchState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready'
      readonly results: CatalogSearchResults
      /** True while a "load more" (cursor-append) request is in flight
       * (UID-253's infinite-scroll/"load more" storefront pagination, kept
       * per ADR-0089 Decision 2). Distinct from `status: 'loading'`, which
       * REPLACES the grid (a fresh search) — appending never hides the
       * already-loaded rows. */
      readonly isLoadingMore: boolean
      /** Set when a "load more" request fails. The already-loaded page stays
       * on screen (unlike a first-page failure, which is a full `error`
       * state) — a failed append is a retryable annoyance, not a reason to
       * throw away results the shopper already has. */
      readonly loadMoreError: string | null
    }

export interface UseCatalogSearchResult {
  readonly state: CatalogSearchState
  /** Runs a fresh search for `query`, resetting to page 1. Composes with
   * whatever category is currently selected (`setCategory`). */
  readonly search: (query: string) => void
  /** Direct/random-access jump to a 1-based page (numbered-page clicks, and
   * Previous — UID-227's offset path). */
  readonly goToPage: (page: number) => void
  /** Advances via the last response's opaque `nextCursor` (UID-227's cursor
   * path) — the cheaper, stable-under-writes route for sequential paging —
   * and APPENDS the new page onto the already-loaded items (UID-253 "load
   * more"), rather than replacing them the way `search`/`setCategory`/
   * `goToPage` do. A no-op when the current page is already the last one. */
  readonly goToNextPage: () => void
  readonly currentPage: number
  readonly pageSize: number
  /** The active category filter, or `null` for "All categories" (UID-33). */
  readonly selectedCategory: string | null
  /**
   * Selects (or clears, via `null`) the category filter and re-runs the
   * search at page 1, composed with whatever keyword is already submitted.
   * A category-only browse (no keyword ever submitted) is valid — only
   * clearing the category AND having no keyword returns to `idle` (there is
   * nothing to browse or search for).
   */
  readonly setCategory: (category: string | null) => void
  /** Distinct catalog category labels for the browse facet, sorted; empty
   * until `listCategories()` resolves (or forever, if it fails — see the
   * doc comment on the effect below). */
  readonly categories: readonly string[]
  readonly categoriesLoading: boolean
}

/**
 * Drives the catalog search screen through a `CatalogSearchService` (UID-227
 * update: total count + numbered pages). Cursor stays the PRIMARY navigation
 * for advancing forward (`goToNextPage`); `goToPage` is the secondary,
 * explicit offset-based jump for numbered-page clicks and Previous — mirroring
 * the hybrid resolution on the ticket. `currentPage` is tracked here in FE
 * state, not derived from the wire response: the backend doesn't report
 * "which page a cursor lands on" (that would need its own count query), and
 * the FE already knows exactly which page each action lands on (current+1
 * for Next, the clicked number for a jump).
 */
export function useCatalogSearch(
  service: CatalogSearchService = defaultCatalogSearchService,
  pageSize: number = DEFAULT_PAGE_SIZE,
): UseCatalogSearchResult {
  const [state, setState] = useState<CatalogSearchState>({ status: 'idle' })
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [categories, setCategories] = useState<readonly string[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const serviceRef = useRef(service)
  serviceRef.current = service
  const keywordRef = useRef('')
  const categoryRef = useRef<string | null>(null)

  // Fetch the category-browse facet once on mount. Query-independent (every
  // category in the catalog, not scoped to a keyword), so it isn't re-run on
  // every search. Failing quietly to an empty list — rather than surfacing a
  // second error state alongside the main search's — keeps a categories
  // outage from blocking keyword search, which has no dependency on it.
  useEffect(() => {
    let cancelled = false
    setCategoriesLoading(true)
    serviceRef.current
      .listCategories()
      .then((result) => {
        if (!cancelled) setCategories(result.map((category) => category.name))
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Deliberately once-only (mirrors "query-independent" above) — reads
    // `service` only via the ref, so there is no reactive dependency to list.
  }, [])

  const run = useCallback(
    (
      options: CatalogSearchOptions,
      resolvedPage: number,
      mode: 'replace' | 'append' = 'replace',
    ) => {
      if (mode === 'append') {
        // "Load more" (UID-253): keep the already-loaded grid on screen and
        // just flag it busy — never regress to the full-page `loading`
        // skeleton, which would hide rows the shopper already has.
        setState((current) =>
          current.status === 'ready'
            ? { ...current, isLoadingMore: true, loadMoreError: null }
            : current,
        )
      } else {
        setState({ status: 'loading' })
      }
      serviceRef.current
        .search(keywordRef.current, {
          ...options,
          limit: pageSize,
          ...(categoryRef.current ? { category: categoryRef.current } : {}),
        })
        .then((results) => {
          setState((current) => {
            if (mode === 'append' && current.status === 'ready') {
              return {
                status: 'ready',
                results: { ...results, items: [...current.results.items, ...results.items] },
                isLoadingMore: false,
                loadMoreError: null,
              }
            }
            return { status: 'ready', results, isLoadingMore: false, loadMoreError: null }
          })
          setCurrentPage(resolvedPage)
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error ? error.message : 'We couldn’t search the catalog. Try again.'
          setState((current) => {
            if (mode === 'append' && current.status === 'ready') {
              return { ...current, isLoadingMore: false, loadMoreError: message }
            }
            return { status: 'error', message }
          })
        })
    },
    [pageSize],
  )

  const search = useCallback(
    (query: string) => {
      keywordRef.current = query
      run({ page: 1 }, 1)
    },
    [run],
  )

  const goToPage = useCallback(
    (page: number) => {
      run({ page }, page)
    },
    [run],
  )

  const goToNextPage = useCallback(() => {
    if (state.status === 'ready' && state.results.nextCursor !== null && !state.isLoadingMore) {
      run({ cursor: state.results.nextCursor }, currentPage + 1, 'append')
    }
  }, [run, currentPage, state])

  const setCategory = useCallback(
    (category: string | null) => {
      categoryRef.current = category
      setSelectedCategory(category)
      // UID-253: neither a keyword nor a category is now a VALID request —
      // the "All products" storefront's default unfiltered browse of the
      // whole catalog (routes.ts no longer 400s on "neither"). Always run
      // the search; there is no more idle "nothing selected yet" state to
      // fall back to.
      run({ page: 1 }, 1)
    },
    [run],
  )

  return {
    state,
    search,
    goToPage,
    goToNextPage,
    currentPage,
    pageSize,
    selectedCategory,
    setCategory,
    categories,
    categoriesLoading,
  }
}
